import React, { useCallback, useEffect } from 'react'
import { actionCreators } from '@/store/InstitutionReportGroup'
import { ButtonGroupCustom, Main, MainBody, MainTop, MainTopTitle, WhiteBox } from '@/components/Layout'
import Resx from '@/components/Resx'
import ErrorBoundary from '@/components/ErrorBoundary'
import InstitutionReportDownloadButtons from './institutionReportDownloadButtons'
import { BsrsGrid, Column, commandColumnProps } from '@/components/BsrsGrid'
import context from '@/context'
import { format } from '@/utils/StringUtilities'
import ReportAttachments from '@/components/reporting/reportAttachments'
import ReportSubmissionGroupNotes from '@/components/reporting/submissions/notes/detail'
import ReportComments from '@/components/reporting/reportComments'
import LoadingIndicator from '@/components/LoadingIndicator'
import { formatDate } from '@/utils/dateUtilities'
import Name from '@/components/Name'
import { Col, Row } from 'reactstrap'
import { InfoContent, InfoLabel, InfoRow } from '@/components/InfoRow'
import { Button } from '@/components/button'
import { openConfirmDialog } from '@/utils/dialogHelpers'
import { useLocalization } from '@/hooks/localization'
import { usePermissionChecker } from '@/hooks/usePermissionChecker'
import { useReduxState } from '@/hooks/reduxState'
import { useDispatch } from '@/hooks/dispatch'
import { useSignalR } from '@/hooks/useSignalR'

const InstitutionReportsPage = function InstitutionReportsPage (props) {
  const { parentId } = props.match.params
  const { forecasting, addAttachments } = props
  const { institutionReportGroup } = useReduxState(useCallback(state => state, []))
  const [get] = useDispatch([actionCreators.get])
  const { localize } = useLocalization()
  const isGranted = usePermissionChecker()

  const reload = useCallback(() => {
    get(parentId)
  }, [get, parentId])
  const onForceGenerateClick = useCallback(() => {
    openConfirmDialog({
      onAction: () =>
        context.api.institutionReportGroupService.forceRegenerateExportFiles(parentId)
    })
  }, [parentId])

  useSignalR('institutionExportsRegenerated', reload)

  useEffect(reload, [reload])

  if (institutionReportGroup.fetching === true || !institutionReportGroup.institutionReportGroup) {
    return <LoadingIndicator />
  }

  return (
    <Main>
      <MainTop>
        <MainTopTitle backUrl='/icbs-report-groups'>
          <Resx name='Reporting.InstitutionReportGroupsTitle' />
        </MainTopTitle>
      </MainTop>
      <MainBody transparent>
        <React.Fragment>
          <ErrorBoundary>
            <WhiteBox header='Reporting.General'>
              <Row>
                <Col sm='6'>
                  <InfoRow>
                    <InfoLabel><Resx name='Common.Name' /></InfoLabel>
                    <InfoContent><Name data={institutionReportGroup.institutionReportGroup} /></InfoContent>
                  </InfoRow>
                </Col>
                <Col sm='6'>
                  <InfoRow>
                    <InfoLabel><Resx name='Common.CreationTime' /></InfoLabel>
                    <InfoContent>{formatDate(institutionReportGroup.institutionReportGroup.creationTime)}</InfoContent>
                  </InfoRow>
                </Col>
              </Row>
            </WhiteBox>
          </ErrorBoundary>
          {isGranted('CanAccessManagementTools') && <WhiteBox header={'Reporting.ReportManagementTool'}>
            <ButtonGroupCustom>
              <Button onClick={onForceGenerateClick} icon='save'><Resx
                name='Reporting.RegenerateAllExports' /></Button>
            </ButtonGroupCustom>
          </WhiteBox>}
          <WhiteBox
            header='Reporting.Sheets'
            buttons={<ErrorBoundary>
              <InstitutionReportDownloadButtons
                institutionReportGroup={institutionReportGroup.institutionReportGroup} />
            </ErrorBoundary>}
          >
            <BsrsGrid defaultSort={[{ field: 'order', dir: 'asc' }]}
              dataSource={request => context.api.institutionReportService.getTableData(parentId, localize('Common.CBI'), forecasting, request)}>
              <Column field='order' title={localize('Common.Order')} filter='numeric' editable={false} />
              <Column field='name' title={localize('Common.ArabicName')} editable={false} />
              <Column field='latinName' title={localize('Common.LatinName')} editable={false} />
              <Column {...commandColumnProps({ detailUrl: x => format('/icbs-report-groups/{0}/icbs-reports/{1}', parentId, x.id) })}
                title=' ' width={150} />
            </BsrsGrid>
          </WhiteBox>

          <ErrorBoundary>
            <ReportAttachments
              disabled
              key={institutionReportGroup.attachmentsKey}
              dataSource={() => context.api.institutionReportGroupService.getAttachments(parentId)}
              downloadAction={context.api.institutionReportGroupService.download}
              parentId={parentId}
              addAttachments={addAttachments}
              removeAttachment={id => props.removeAttachments({ entityId: parentId, fileIds: [id] })}
              adding={institutionReportGroup.adding} />
          </ErrorBoundary>

          <ErrorBoundary>
            <ReportSubmissionGroupNotes parentId={parentId} />
          </ErrorBoundary>

          <ErrorBoundary>
            <ReportComments hideHeader parentId={parentId} disabled={false} showExternalVisibilityCheckbox />
          </ErrorBoundary>
        </React.Fragment>
      </MainBody>
    </Main>
  )
}

export default InstitutionReportsPage



// WEBPACK FOOTER //
// ./src/internal-app/pages/institutionReports/institutionReports.js